import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import UrlUtils from '@shared/Utils/url.utils';
import { TaskActions } from '@tasks/state/actions';
import { TaskState } from '@tasks/state/state';
import {
  LAST_TASK_VIEW_ID_ACCESSED_KEY,
  LAST_TASK_VIEW_TYPE_ACCESSED_KEY,
  TaskRelatedToModule,
} from '@tasks/tasks.types';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { filter, map, merge, of, switchMap } from 'rxjs';
const VALID_TASK_VIEW_TYPES = ['list', 'gantt', 'kanban', 'calendar'];
@Component({
  selector: 'app-tasks-splash',
  standalone: true,
  imports: [RouterModule, RouterOutlet],
  template: `<router-outlet></router-outlet>`,
  styleUrl: './tasks-splash.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TasksSplashComponent implements OnInit {
  private store = inject(Store);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private actions$ = inject(Actions);
  constructor() {}

  ngOnInit(): void {
    this.actions$
      .pipe(takeUntilDestroyed(this.destroyRef), ofActionCompleted(TaskActions.SetViewType))
      .subscribe((viewType) => {
        const currentTaskView = this.store.selectSnapshot(TaskState.getTaskView);
        const localStorageKey = this.getLocalStorageKeys();
        UrlUtils.saveToLocalStorage(localStorageKey.viewType, viewType.action.viewType);
        this.router.navigate(['./', currentTaskView.id, viewType.action.viewType], { relativeTo: this.activatedRoute });
      });

    this.actions$
      .pipe(takeUntilDestroyed(this.destroyRef), ofActionCompleted(TaskActions.SetTaskView))
      .subscribe((viewType) => {
        const isAggregatedTasks =
          this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType === TaskRelatedToModule.AggregatedTasks;
        if (isAggregatedTasks && viewType.action.taskView?.id) {
          const localStorageKey = this.getLocalStorageKeys();
          UrlUtils.saveToLocalStorage(localStorageKey.id, viewType.action.taskView?.id);
        }
      });

    const navigationEnd$ = this.router.events.pipe(
      filter((event) => {
        return event instanceof NavigationEnd && this.activatedRoute.children.length === 0;
      }),
    );

    const paramsChange$ = this.activatedRoute.params;

    merge(navigationEnd$, paramsChange$)
      .pipe(
        map(() => this.activatedRoute.snapshot.params),
        switchMap(({ customObjectId, taskRelatedTo, taskRelatedToId }) => {
          const customObjectName = this.store
            .selectSnapshot(ObjectsManagerState.get)
            .find((x) => x.id === +customObjectId)?.name;
          return this.store.dispatch(new TaskActions.SetRelatedTo(taskRelatedTo, taskRelatedToId, customObjectName));
        }),
        switchMap(() => this.store.dispatch(new TaskActions.GetSidebarItems())),
        switchMap(() => {
          const taskListId = this.activatedRoute?.firstChild?.snapshot.params?.taskListId;
          const viewType = this.activatedRoute?.firstChild?.firstChild?.snapshot.url[0].path;
          return of({ taskListId, viewType });
        }),
      )
      .subscribe((childParams: any) => {
        const taskViews = this.store.selectSnapshot(TaskState.getSidebarItems);
        const localStorageKey = this.getLocalStorageKeys();
        const lastId = childParams.taskListId || UrlUtils.getFromLocalStorage(localStorageKey.id);
        let lastType = childParams.viewType || UrlUtils.getFromLocalStorage(localStorageKey.viewType) || 'list';
        if (!VALID_TASK_VIEW_TYPES.includes(lastType)) {
          lastType = 'list';
        }

        if (lastId && taskViews.length > 0) {
          let view = taskViews.find((view) => +view.id === +lastId);
          if (!view) {
            view = taskViews[0];
          }
          this.store.dispatch([new TaskActions.SetTaskView(view), new TaskActions.SetViewType(lastType)]);
        } else {
          if (!taskViews || taskViews.length === 0) {
            this.router.navigate(['./', -1, 'list'], { relativeTo: this.activatedRoute });
            return;
          }

          let taskView = taskViews[0];
          let id = taskView.id;
          this.router.navigate(['./', id, lastType], { relativeTo: this.activatedRoute });
        }
      });
  }

  private getLocalStorageKeys(): { id: string; viewType: string } {
    const relatedToTypeModule = this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType;
    return {
      id: LAST_TASK_VIEW_ID_ACCESSED_KEY.replace('$RELATED_TO_MODULE$', relatedToTypeModule),
      viewType: LAST_TASK_VIEW_TYPE_ACCESSED_KEY,
    };
  }
}
